import DefaultButton from '@components/Atoms/DefaultButton';
import Modal from '@components/Modal';
import ResponseModal from '@components/ModalContent/CommentResponse';
import ViewCommentModal from '@components/ModalContent/ViewComment';
import { getColorClassName } from '@helpers/color.helper';
import { useQueryClient } from '@tanstack/react-query';
import { newFormatDate } from '@utils/date.util';
import { truncateString } from '@utils/string.util';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import styles from './CommentsActionCard.module.scss';

const CommentsActionCard: React.FunctionComponent<ActionsCardProps> = (props) => {
  const {
    focusArea,
    focusAreaColor,
    action,
    score,
    categoryName,
    completed,
    createdDate,
    addToCompleted,
    showResponseButton,
    saveResponseProps,
    refetchAllThoughts,
  } = props;
  const queryClient = useQueryClient();

  const [openResponseModal, setOpenResponseModal] = useState(false);
  const [openViewCommentsModal, setOpenViewCommentsModal] = useState(false);
  const [openAcknowledgeModal, setOpenAcknowledgeModal] = useState(false);
  const [error, setError] = useState(false);
  const [actionsCheckbox, setActionCheckbox] = useState(false);
  const [thoughtResponseText, setThoughtResponseText] = useState<any>('');

  const colorClassName = getColorClassName(focusAreaColor);

  const styleClasses = classNames([styles.hsActionsCard, styles[colorClassName]]);

  // const styleClasses = classNames([
  //   styles.hsActionsCard,
  //   focusAreaColor === '#BEAAF3' && styles.calmLilac,
  //   focusAreaColor === '#019DF2' && styles.skyBlue,
  //   focusAreaColor === '#FCD300' && styles.sunYellow,
  //   focusAreaColor === '#8EEA36' && styles.zingyGreen,
  //   focusAreaColor === '#06BF97' && styles.aquaGreen,
  //   focusAreaColor === '#FF8A4A' && styles.warmOrange,
  // ]);

  const handleCloseResponseModal = useCallback(() => {
    setOpenResponseModal(false);
  }, []);

  const handleOpenResponseModal = useCallback(() => {
    setOpenResponseModal(true);
  }, []);

  const handleOpenAcknowledgeModal = useCallback(() => {
    setOpenAcknowledgeModal(true);
  }, []);

  const handleCloseAcknowledgeModal = useCallback(() => {
    setOpenAcknowledgeModal(false);
  }, []);

  const handleSubmitAcknowledge = useCallback(async () => {
    saveResponseProps('Acknowledged');
    setOpenAcknowledgeModal(false);
    if (refetchAllThoughts) {
      await refetchAllThoughts();
    }
  }, [saveResponseProps]);

  const handleSubmitComment = useCallback(
    async (data: any, methods: any) => {
      setOpenResponseModal(false);
      saveResponseProps(data.content);

      methods.reset();
    },
    [saveResponseProps],
  );

  const handleOpenViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(true);
  }, [action]);

  const handleCloseViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(false);
  }, []);

  const isModalView = action?.length >= 75;

  return (
    <div className={styleClasses}>
      <div className={styles.tabContent}>
        {showResponseButton && (
          <>
            <div className={styles.hsActionsCardTitle}>
              <div className={styles.dateContainer}>
                <p className={styles.formattedDate}>{newFormatDate(createdDate)}</p>
                <p className={styles.focusArea}>{focusArea}</p>
              </div>

              <p className={styles.categorySection}>
                <p>{categoryName}</p>
                <span>{score}</span>
              </p>
            </div>
            <div className={styles.hsActionsCardTitle}>
              <p className={styles.truncateSection}>
                {truncateString(action, 75)}
                {isModalView && (
                  <a className={styles.viewMore} onClick={handleOpenViewCommentModal}>
                    View More
                  </a>
                )}
              </p>
            </div>
          </>
        )}

        {!showResponseButton && (
          <>
            <div className={styles.hsActionsCardTitle}>
              <div className={styles.dateContainer}>
                <p className={styles.formattedDate}>{newFormatDate(createdDate)}</p>
                <p className={styles.focusArea}>{focusArea}</p>
              </div>

              <p className={styles.categorySection2}>
                <p>{categoryName}</p>
                <span>{score}</span>
              </p>
            </div>

            <div className={styles.hsActionsCardTitle}>
              <p className={styles.truncateSection}>
                {truncateString(action, 75)}
                {isModalView && (
                  <a className={styles.viewMore} onClick={handleOpenViewCommentModal}>
                    View More
                  </a>
                )}
              </p>
            </div>
          </>
        )}
      </div>

      {!showResponseButton && (
        <>
          <div className={styles.lineBetween} />
          <div className={styles.buttonContainer}>
            <div className={styles.inputContainer}>
              <DefaultButton
                disabled={showResponseButton}
                variant="primary"
                onClick={handleOpenAcknowledgeModal}
              >
                Acknowledge
              </DefaultButton>
            </div>
            <div className={styles.inputContainer}>
              <DefaultButton
                disabled={showResponseButton}
                variant="primary"
                onClick={handleOpenResponseModal}
              >
                Respond
              </DefaultButton>
            </div>
          </div>
        </>
      )}

      <ResponseModal
        title="Respond to user"
        isOpen={openResponseModal}
        onClose={handleCloseResponseModal}
        onSave={handleSubmitComment}
      />

      <Modal
        open={openAcknowledgeModal}
        setOpen={handleCloseAcknowledgeModal}
        overlay
        onTapBackgroundClose
      >
        <h4 className={styles.title}>Acknowledge Comment</h4>
        <hr />
        <div className="container">
          <div>
            <h4 className={styles.bodyMessage}>
              This will send an “Acknowledged” message to the user and move this comment in
              responded tab.
            </h4>
            <span>
              If you would like to write a specific message to the user, please Cancel and use the
              “Respond” button.
            </span>
          </div>
        </div>
        <hr />

        <div className={styles.actionButtons}>
          <DefaultButton
            type="button"
            onClick={handleCloseAcknowledgeModal}
            className={styles.cancelBtn}
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            type="button"
            onClick={handleSubmitAcknowledge}
            className={styles.confirmBtn}
          >
            Confirm
          </DefaultButton>
        </div>
      </Modal>

      <ViewCommentModal
        title={focusArea}
        isOpen={openViewCommentsModal}
        onClose={handleCloseViewCommentModal}
      >
        {action}
      </ViewCommentModal>
    </div>
  );
};

CommentsActionCard.defaultProps = {
  showResponseButton: false,
  refetchAllThoughts: () => null,
};

type ActionsCardProps = {
  focusArea: string;
  focusAreaColor: any;
  action: string;
  score: number;
  completed: boolean;
  createdDate: string;
  addToCompleted: () => void;
  showResponseButton?: boolean;
  saveResponseProps: (thoughtResponseText: string) => void;
  categoryName: string;
  refetchAllThoughts?: () => void;
};

export default CommentsActionCard;
